import React from 'react';
import { CreditCard, Wallet, Building, Banknote } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const PaymentMethodTotals = ({
  methodTotals,
  totalAmount
}) => {
  const { t } = useTranslation();

  // Debug logging
  console.log('PaymentMethodTotals Debug:', {
    methodTotals,
    totalAmount,
    isArray: Array.isArray(methodTotals),
    length: methodTotals?.length
  });

  // Convert object to array if needed and check if data exists
  let methodTotalsArray = [];

  if (methodTotals && typeof methodTotals === 'object') {
    if (Array.isArray(methodTotals)) {
      methodTotalsArray = methodTotals;
    } else {
      // Convert object to array format
      methodTotalsArray = Object.entries(methodTotals).map(([method, data]) => ({
        method,
        total: data.total,
        count: data.count,
        percentage: totalAmount > 0 ? (data.deposits / totalAmount) * 100 : 0,
        average: data.count > 0 ? data.total / data.count : 0,
        deposits: data.deposits,
        refunds: data.refunds
      }));

      // Sort by total amount descending
      methodTotalsArray.sort((a, b) => b.total - a.total);
    }
  }

  const getMethodIcon = (method) => {
    switch (method) {
      case 'cash':
        return <Banknote size={20} className="text-green-600" />;
      case 'bankTransfer':
        return <Building size={20} className="text-blue-600" />;
      case 'wallet':
        return <Wallet size={20} className="text-purple-600" />;
      case 'egyptianPost':
        return <CreditCard size={20} className="text-orange-600" />;
      default:
        return <CreditCard size={20} className="text-slate-600" />;
    }
  };

  const getMethodColor = (method) => {
    switch (method) {
      case 'cash':
        return 'border-green-200 bg-green-50';
      case 'bankTransfer':
        return 'border-blue-200 bg-blue-50';
      case 'wallet':
        return 'border-purple-200 bg-purple-50';
      case 'egyptianPost':
        return 'border-orange-200 bg-orange-50';
      default:
        return 'border-slate-200 bg-slate-50';
    }
  };

  // Enhanced validation
  if (!methodTotalsArray || methodTotalsArray.length === 0) {
    console.log('PaymentMethodTotals: No data to display');
    return null;
  }

  console.log('Processed methodTotalsArray:', methodTotalsArray);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        {t('paymentMethodTotals')}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {methodTotalsArray.map((methodTotal, index) => {
          // Debug each method total

          return (
            <div
              key={methodTotal.method || index}
              className={`p-4 rounded-xl border-2 ${getMethodColor(methodTotal.method)} hover:shadow-md transition-all duration-200`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm mb-1">
                    {t(methodTotal.method) || methodTotal.method || 'Unknown Method'}
                  </h4>
                  <p className="text-2xl font-bold text-slate-900">
                    {(methodTotal.total || 0).toLocaleString()} {t('EGP')}
                  </p>
                </div>
                {getMethodIcon(methodTotal.method)}
              </div>

              <div className="space-y-1 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span>{t('recordCount')}:</span>
                  <span className="font-medium">{methodTotal.count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('percentage')}:</span>
                  <span className="font-medium">{(methodTotal.percentage || 0).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('averagePerRecord')}:</span>
                  <span className="font-medium">{(methodTotal.average || 0).toLocaleString()} {t('EGP')}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentMethodTotals;